import api from "../service/api";
import { WarehouseTask } from "../types/warehouse";
import { createApiStore } from "./genericStore";
import { useAuthStore } from "./authStore";


// --- STORE TANIMI ---

// 1. Base Store Oluşturma (Sadece /warehouse-task endpoint'i için)
const useTaskBaseStore = createApiStore<WarehouseTask>("warehouse-task");

// Eksikler için ayrı Store (Otomatik olarak /warehouse-shortage/DynamicQuery kullanır)
const useShortageBaseStore = createApiStore<any>("warehouse-shortage");

export const useWarehouseShortageStore = () => {
  const store = useShortageBaseStore();

  return {
    ...store,
    createIndependentList: async (data: any) => {
      const res = await api.post("/warehouse-shortage/create-independent-list", data);
      await store.fetchDataDynamic();
      return res.data;
    },
    assignShortage: async (shortageId: number, data: { assignedUserId: number }) => {
      const res = await api.put(`/warehouse-shortage/${shortageId}/assign`, data);
      await store.fetchDataDynamic();
      return res.data;
    },
    markUnavailable: async (shortageId: number) => {
      const res = await api.put(`/warehouse-shortage/${shortageId}/mark-unavailable`);
      await store.fetchDataDynamic();
      return res.data;
    },
    resolveShortage: async (shortageId: number) => {
      const res = await api.put(`/warehouse-shortage/${shortageId}/resolve`);
      await store.fetchDataDynamic();
      return res.data;
    },
    cancelShortage: async (shortageId: number) => {
      const res = await api.put(`/warehouse-shortage/${shortageId}/cancel`);
      await store.fetchDataDynamic();
      return res.data;
    },
    unresolveShortage: async (shortageId: number) => {
      const res = await api.put(`/warehouse-shortage/${shortageId}/unresolve`);
      await store.fetchDataDynamic();
      return res.data;
    },
    getMyHistory: async () => {
      const res = await api.get("/warehouse-shortage/my-history", {
        headers: { "x-silent": "true" }
      });
      return res.data;
    },
    getAssignedToMe: async () => {
      const res = await api.get("/warehouse-shortage/assigned-to-me", {
        headers: { "x-silent": "true" }
      });
      return res.data;
    }
  };
};

// 2. Özel Fonksiyonlarla Genişletilmiş Store
export const useWarehouseTaskStore = () => {
  const store = useTaskBaseStore();

  return {
    ...store,

    /**
     * MODES: POOL (Boştaki İşler) veya MY_TASKS (Üzerimdeki İşler)
     * Backend'deki dynamicTasks metoduna mode parametresi gönderir.
     */
    fetchByMode: async (mode: "POOL" | "MY_TASKS") => {
      // Mevcut parametreleri al, eksikse varsayılan ata
      const currentParams = store.queryParams || {};

      return await store.fetchDataDynamic({
        ...currentParams,
        pageIndex: currentParams.pageIndex || 1, // 👈 Kritik: Boş gitmesin
        pageSize: currentParams.pageSize || 10,  // 👈 Kritik: Boş gitmesin
        mode: mode,
        light: true // 🌟 Liste sorgusunu hafiflet
      } as any);
    },
    
    // override fetchDataDynamic to inject light: true and client-side precision filter for Atadıklarım
    fetchDataDynamic: async (params?: any) => {
      const mergedParams = { ...store.queryParams, ...(params || {}), light: true };
      const res = await store.fetchDataDynamic(mergedParams);

      // 🎯 "Atadıklarım" Filtresi Hassaslaştırma:
      // Prisma backend'i actions.some.userId ve actions.some.description'ı ayrı alt sorgularda çalıştırdığı için
      // aynı aksiyon kaydında gerçekleşip gerçekleşmediğini teyit eder.
      const isMyAssignedFilter = JSON.stringify(mergedParams?.filter || "").includes("Personel:");
      if (isMyAssignedFilter && res && Array.isArray(res.items)) {
        const myId = useAuthStore.getState().user?.id;
        if (myId) {
          const filteredItems = res.items.filter((task: any) => {
            if (task.assignedUserId === myId) return true;
            return task.actions?.some(
              (a: any) =>
                a.userId === myId &&
                (a.description?.includes("Personel:") || a.actionType === "ASSIGNED")
            );
          });
          useTaskBaseStore.setState({ data: filteredItems, total: filteredItems.length });
          return { items: filteredItems, total: filteredItems.length };
        }
      }

      return res;
    },
    completeBulk: async (ids: any) => {
      // axios/fetch ile body'de ids'i gönderiyoruz. 
      // Tanımsızsa backend'de optional (undefined) olarak işlenir.
      await api.put('/warehouse-task/complete-bulk', { ids });
    },
    completeAll: async () => {
      await api.put('/warehouse-task/complete-all');
    },
    getSummary: async () => {
      const res = await api.get("/warehouse/task/stats/summary", {
        headers: { "x-silent": "true" }
      });
      console.log(res)
      return res.data; // Buradan dönen veri sayfadaki state'i besleyecek
    }, adminUpdate: async (id: number, data: any) => {
      const res = await api.put(`/warehouse-task/${id}/admin-update`, data);
      await store.fetchDataDynamic();
      return res.data;
    }, autoAssign: async () => {
      const res = await api.post("/warehouse/task/auto-assign");
      await store.fetchDataDynamic();
      return res.data;
    }, getSingleTask: async (id: number, isAdmin = false) => {
      const res = await api.get(
        isAdmin
          ? `/warehouse-task/admin/${id}`
          : `/warehouse-task/${id}`
      );
      return res.data;
    },

    /**
     * GÖREVİ BAŞLAT (ASSIGNED)
     */
    startTask: async (taskId: number) => {
      const res = await api.put(`/warehouse-task/${taskId}/start`);
      // Listeyi güncellerken mevcut filtreleri koru
      await store.fetchDataDynamic(store.queryParams);
      return res.data;
    },
    resetTask: async (taskId: number) => {
      const res = await api.put(`/warehouse-task/${taskId}/reset`);
      // Listeyi ve parametreleri tazele
      await store.fetchDataDynamic(store.queryParams);
      return res.data;
    },
    /**
     * ÜRÜN TOPLA (ITEM_PICKED)
     */
    pickItem: async (taskId: number, data: { stockId: number; variantId?: number | null; quantity: number }) => {
      const payload: any = {
        stockId: Number(data.stockId),
        quantity: Number(data.quantity),
      };
      if (data.variantId) {
        payload.variantId = Number(data.variantId);
      }
      const res = await api.put(`/warehouse-task/${taskId}/pick-item`, payload);
      await store.fetchDataDynamic();
      return res.data;
    },

    /**
     * ÜRÜN TOPLAMAYI GERİ AL (UNPICK)
     */
    unpickItem: async (taskId: number, data: { stockId: number; variantId?: number | null; quantity: number }) => {
      const payload: any = {
        stockId: Number(data.stockId),
        quantity: Number(data.quantity),
      };
      if (data.variantId) {
        payload.variantId = Number(data.variantId);
      }
      const res = await api.put(`/warehouse-task/${taskId}/unpick-item`, payload);
      await store.fetchDataDynamic();
      return res.data;
    },

    /**
     * ÜRÜN PAKETLE (ITEM_PACKED)
     */
    packItem: async (taskId: number, data: { stockId: number; variantId?: number | null; quantity: number }) => {
      const payload: any = {
        stockId: Number(data.stockId),
        quantity: Number(data.quantity),
      };
      if (data.variantId) {
        payload.variantId = Number(data.variantId);
      }
      const res = await api.put(`/warehouse-task/${taskId}/pack-item`, payload);
      await store.fetchDataDynamic();
      return res.data;
    },

    /**
     * ÜRÜN PAKETLEMEYİ GERİ AL (UNPACK)
     */
    unpackItem: async (taskId: number, data: { stockId: number; variantId?: number | null; quantity: number }) => {
      const payload: any = {
        stockId: Number(data.stockId),
        quantity: Number(data.quantity),
      };
      if (data.variantId) {
        payload.variantId = Number(data.variantId);
      }
      const res = await api.put(`/warehouse-task/${taskId}/unpack-item`, payload);
      await store.fetchDataDynamic();
      return res.data;
    },

    /**
     * PAKETLEMEYİ ÜZERİNE AL (CLAIM)
     */
    claimPackaging: async (taskId: number) => {
      const res = await api.put(`/warehouse-task/${taskId}/claim-packaging`);
      await store.fetchDataDynamic();
      return res.data;
    },

    /**
     * EKSİK BİLDİR (SHORTAGE)
     */
    reportShortage: async (taskId: number, data: { taskItemId: number; missingQty: number; note: string }) => {
      const res = await api.put(`/warehouse-task/${taskId}/report-shortage`, data);
      await store.fetchDataDynamic();
      return res.data;
    },



    /**
     * TOPLAYICIYA REDDET (Mevcut)
     */
    rejectToPicker: async (taskId: number, data: { note: string }) => {
      const res = await api.put(`/warehouse-task/${taskId}/reject-to-picker`, data);
      return res.data;
    },


    /**
     * PAKETLEMEYİ BİTİR (FINISH)
     */
    finishPackaging: async (taskId: number) => {
      const res = await api.put(`/warehouse-task/${taskId}/finish-packaging`, {});
      await store.fetchDataDynamic();
      return res.data;
    },

    /**
     * HIZLI BİTİR (QUICK COMPLETE)
     */
    quickComplete: async (taskId: number) => {
      const res = await api.put(`/warehouse-task/${taskId}/quick-complete`);
      await store.fetchDataDynamic();
      return res.data;
    },

    /**
     * PERSONEL LİSTESİ (Atama işlemleri için)
     */
    // taskStore içindeki getStaffList (Eğer detayları da almak istersen böyle güncellenmeli)
    getStaffList: async () => {
      const requestBody = {
        filter: { field: "role.name", operator: "eq", value: "warehouse_staff" }
      };
      const res = await api.post("/user/DynamicQuery", requestBody, {
        params: { pageIndex: 1, pageSize: 1000 },
        headers: { "x-silent": "true" }
      });

      return res.data.items?.map((u: any) => ({
        label: u.name,
        value: u.id,
        // JSON yapına göre staffDetail içinden çekiyoruz:
        isPicker: u.staffDetail?.isPicker,
        isPacker: u.staffDetail?.isPacker,
        hasForklift: u.staffDetail?.hasForklift,
        canLiftHeavy: u.staffDetail?.canLiftHeavy,
        experienceLevel: u.staffDetail?.experienceLevel
      })) || [];
    },
    /**
     * GÖREVİ SİL (Soft Delete)
     * Görevi sistemden pasife çeker.
     */
    deleteTask: async (taskId: number) => {
      // Backend'deki @Put("{taskId}/delete") endpoint'ine istek atar
      const res = await api.put(`/warehouse-task/${taskId}/delete`);
      // Listeyi günceller (Mevcut filtreleri koruyarak)
      await store.fetchDataDynamic(store.queryParams);
      return res.data;
    },

    /**
     * AKSİYONU SİL (Hard Delete)
     * Personelin hatalı girdiği bir log kaydını (aksiyonu) siler.
     */
    deleteAction: async (actionId: number) => {
      // Backend'deki @Delete("action/{actionId}") endpoint'ine istek atar
      const res = await api.delete(`/warehouse-task/action/${actionId}`);
      // Aksiyon silindikten sonra veriyi tazelemek için (özellikle detay sayfasındaysan)
      // Eğer tekil görev detayı store'da tutuluyorsa o da güncellenebilir.
      await store.fetchDataDynamic(store.queryParams);
      return res.data;
    },

    /**
     * KARGO FİRMALARINI GETİR
     */
    getShipmentFirms: async () => {
      const res = await api.get("/order/shipment-firms", {
         headers: { "x-silent": "true" }
      });
      return res.data;
    },

    /**
     * SİPARİŞ KAYNAKLARINI (ORDER SOURCES) GETİR
     * Backend API'sinden (/warehouse-task/sources veya /order/sources) kaynak listesini çeker.
     */
    getOrderSources: async () => {
      try {
        const res = await api.get("/warehouse-task/sources", {
          headers: { "x-silent": "true" }
        });
        if (Array.isArray(res.data)) {
          return res.data.map((item: any) =>
            typeof item === "string" ? { label: item, value: item } : item
          );
        }
        return [];
      } catch (error) {
        try {
          const altRes = await api.get("/order/sources", {
            headers: { "x-silent": "true" }
          });
          if (Array.isArray(altRes.data)) {
            return altRes.data.map((item: any) =>
              typeof item === "string" ? { label: item, value: item } : item
            );
          }
        } catch (_) {}
        return [];
      }
    }

  };

};